/**
 * Shared constants for devtools UI.
 *
 * ANVIL_ACCOUNTS are NOT defined here — they come from the backend context
 * (EvmBackendContext.accounts). Each backend provides its own pre-funded
 * accounts (Anvil, Tevm, fork, etc.).
 */

import { sortTokens } from "@gotts.ai/chain";

import type { Address } from "viem";

// ---------------------------------------------------------------------------
// Primitive constants
// ---------------------------------------------------------------------------

export const MAX_UINT256 = 2n ** 256n - 1n;
export const ZERO_ADDRESS =
  "0x0000000000000000000000000000000000000000" as Address;

// ---------------------------------------------------------------------------
// sqrtPriceX96 limits (effectively "no limit" for V3/V4 swaps)
// ---------------------------------------------------------------------------

export const MIN_SQRT_PRICE_LIMIT = 4295128739n + 1n;
export const MAX_SQRT_PRICE_LIMIT =
  1461446703485210103287273052203988822378723970342n - 1n;

// ---------------------------------------------------------------------------
// sqrtPriceX96 initial prices (devenv mock prices)
// ---------------------------------------------------------------------------

const SQRT_PRICE_1_1 = 79228162514264337593543950336n;
const SQRT_PRICE_STABLE_WETH = 1771595571142957166518320255467n;
const SQRT_PRICE_WETH_STABLE = 3543191142285914493284720n;
const SQRT_PRICE_WBTC_WETH = 306731823225498842930880724992n;
const SQRT_PRICE_WETH_WBTC = 20482261976833980437376512n;

// ---------------------------------------------------------------------------
// Token metadata
// ---------------------------------------------------------------------------

export const TOKEN_DECIMALS: Record<string, number> = {
  WETH: 18,
  USDC: 6,
  USDT: 6,
  DAI: 18,
  WBTC: 8,
};

export const TOKEN_SYMBOLS = ["WETH", "USDC", "USDT", "DAI", "WBTC"] as const;
export type TokenSymbol = (typeof TOKEN_SYMBOLS)[number];

export const DEVENV_TOKEN_PRICES: Record<string, number> = {
  WETH: 2000,
  USDC: 1,
  USDT: 1,
  DAI: 1,
  WBTC: 30000,
};

export const V3_FEE_TIERS = [
  { label: "1 bps (stablecoins)", value: 100, tickSpacing: 1 },
  { label: "5 bps", value: 500, tickSpacing: 10 },
  { label: "30 bps (standard)", value: 3000, tickSpacing: 60 },
  { label: "100 bps (exotic)", value: 10000, tickSpacing: 200 },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export { sortTokens };

/** Fresh deadline 1 hour from now. */
export function deadline(): bigint {
  return BigInt(Math.floor(Date.now() / 1000) + 3600);
}

export function tickFromSqrtPriceX96(sqrtPriceX96: bigint): number {
  const Q96 = Number(2n ** 96n);
  const sqrtPrice = Number(sqrtPriceX96) / Q96;
  const price = sqrtPrice * sqrtPrice;
  return Math.floor(Math.log(price) / Math.log(1.0001));
}

export function alignTick(tick: number, spacing: number): number {
  return Math.floor(tick / spacing) * spacing;
}

export function getInitialSqrtPriceX96(
  token0: Address,
  token1: Address,
  tokens: { weth?: Address; wbtc?: Address; WETH?: Address; WBTC?: Address },
): bigint {
  const weth = tokens.weth?.toLowerCase() ?? tokens.WETH?.toLowerCase();
  const wbtc = tokens.wbtc?.toLowerCase() ?? tokens.WBTC?.toLowerCase();
  const t0 = token0.toLowerCase();
  const t1 = token1.toLowerCase();

  const hasWeth = t0 === weth || t1 === weth;
  const hasWbtc = t0 === wbtc || t1 === wbtc;

  if (!hasWeth) return SQRT_PRICE_1_1;
  if (hasWbtc) {
    return t0 === wbtc ? SQRT_PRICE_WBTC_WETH : SQRT_PRICE_WETH_WBTC;
  }
  return t0 === weth ? SQRT_PRICE_WETH_STABLE : SQRT_PRICE_STABLE_WETH;
}

// ---------------------------------------------------------------------------
// V4 Liquidity Math
// ---------------------------------------------------------------------------

const Q96 = 2n ** 96n;
const MIN_SQRT_PRICE = 4295128739n;
const MAX_SQRT_PRICE = 1461446703485210103287273052203988822378723970341n;
const MIN_TICK = -887272;
const MAX_TICK = 887272;

export function tickToSqrtPriceX96(tick: number): bigint {
  if (tick <= MIN_TICK) return MIN_SQRT_PRICE;
  if (tick >= MAX_TICK) return MAX_SQRT_PRICE;

  if (tick < -400000) {
    const delta = tick - MIN_TICK;
    const factor = Math.pow(1.0001, delta / 2);
    return BigInt(Math.round(Number(MIN_SQRT_PRICE) * factor));
  }

  if (tick > 400000) {
    const Q192 = 1n << 192n;
    const reciprocal = tickToSqrtPriceX96(-tick);
    if (reciprocal === 0n) return MAX_SQRT_PRICE;
    return Q192 / reciprocal;
  }

  const sqrtPrice = Math.pow(1.0001, tick / 2);
  return BigInt(Math.round(sqrtPrice * 2 ** 48)) << 48n;
}

export function liquidityFromAmounts(
  sqrtPriceCurrentX96: bigint,
  tickCurrent: number,
  tickLower: number,
  tickUpper: number,
  amount0: bigint,
  amount1: bigint,
): bigint {
  if (tickLower >= tickUpper) return 0n;
  if (amount0 === 0n && amount1 === 0n) return 0n;

  const sqrtPriceLowerX96 = tickToSqrtPriceX96(tickLower);
  const sqrtPriceUpperX96 = tickToSqrtPriceX96(tickUpper);

  if (tickCurrent < tickLower) {
    if (amount0 === 0n) return 0n;
    const denom = sqrtPriceUpperX96 - sqrtPriceLowerX96;
    if (denom === 0n) return 0n;
    return (amount0 * sqrtPriceLowerX96 * sqrtPriceUpperX96) / (denom * Q96);
  }

  if (tickCurrent >= tickUpper) {
    if (amount1 === 0n) return 0n;
    const denom = sqrtPriceUpperX96 - sqrtPriceLowerX96;
    if (denom === 0n) return 0n;
    return (amount1 * Q96) / denom;
  }

  const denomA = sqrtPriceUpperX96 - sqrtPriceCurrentX96;
  const denomB = sqrtPriceCurrentX96 - sqrtPriceLowerX96;
  const L0 =
    denomA > 0n && amount0 > 0n
      ? (amount0 * sqrtPriceCurrentX96 * sqrtPriceUpperX96) / (denomA * Q96)
      : 0n;
  const L1 = denomB > 0n && amount1 > 0n ? (amount1 * Q96) / denomB : 0n;

  if (L0 === 0n) return L1;
  if (L1 === 0n) return L0;
  return L0 < L1 ? L0 : L1;
}

// ---------------------------------------------------------------------------
// ERC-8004 Reputation categories & tier thresholds
// ---------------------------------------------------------------------------

export const REPUTATION_CATEGORIES = [
  {
    name: "Entry",
    color: "hsl(142 76% 36%)",
    weight: 0.2,
    max: 200,
    description: "Agent registration and identity verification milestones",
  },
  {
    name: "Time",
    color: "hsl(48 96% 53%)",
    weight: 0.15,
    max: 150,
    description: "Duration since registration and active participation time",
  },
  {
    name: "Capital",
    color: "hsl(221 83% 53%)",
    weight: 0.25,
    max: 250,
    description: "Total capital deployed and managed across vaults",
  },
  {
    name: "Behavior",
    color: "hsl(262 83% 58%)",
    weight: 0.25,
    max: 250,
    description: "Governance participation and protocol compliance",
  },
  {
    name: "Ecosystem",
    color: "hsl(0 84% 60%)",
    weight: 0.15,
    max: 150,
    description:
      "Contributions to the ecosystem (tooling, documentation, etc.)",
  },
] as const;

export type Tier =
  | "sovereign"
  | "trusted"
  | "verified"
  | "basic"
  | "unverified";

export function reputationTier(score: number): Tier {
  if (score >= 500) return "sovereign";
  if (score >= 100) return "trusted";
  if (score >= 50) return "verified";
  if (score >= 10) return "basic";
  return "unverified";
}

export const TIER_THRESHOLDS: Record<
  Tier,
  { threshold: string; next?: Tier; nextScore?: number }
> = {
  unverified: { threshold: "0-9", next: "basic", nextScore: 10 },
  basic: { threshold: "10-49", next: "verified", nextScore: 50 },
  verified: { threshold: "50-99", next: "trusted", nextScore: 100 },
  trusted: { threshold: "100-499", next: "sovereign", nextScore: 500 },
  sovereign: { threshold: "500+" },
};

// ---------------------------------------------------------------------------
// Seeded agents (ERC-8004 identity + reputation from devenv seed scripts)
// ---------------------------------------------------------------------------

export const SEEDED_AGENTS = [
  {
    name: "alice",
    index: 1,
    agentId: 1,
    reputation: 500,
    tier: "sovereign" as const,
    role: "vault_manager" as const,
    description: "USDC yield vault manager",
  },
  {
    name: "bob",
    index: 2,
    agentId: 2,
    reputation: 300,
    tier: "trusted" as const,
    role: "vault_manager" as const,
    description: "WETH growth trading agent",
  },
  {
    name: "carol",
    index: 3,
    agentId: 3,
    reputation: 100,
    tier: "trusted" as const,
    role: "vault_manager" as const,
    description: "Multi-asset conservative vault",
  },
  {
    name: "dave",
    index: 4,
    agentId: 4,
    reputation: 50,
    tier: "verified" as const,
    role: "vault_participant" as const,
    description: "Concentrated LP specialist",
  },
  {
    name: "eve",
    index: 5,
    agentId: 5,
    reputation: 10,
    tier: "basic" as const,
    role: "vault_participant" as const,
    description: "New agent, observation mode",
  },
] as const;

export function getAgentForAccountIndex(
  index: number,
): (typeof SEEDED_AGENTS)[number] | undefined {
  return SEEDED_AGENTS.find((a) => a.index === index);
}

// ---------------------------------------------------------------------------
// Vault Constants
// ---------------------------------------------------------------------------

export const FEE_CAPS = {
  MAX_MANAGEMENT_FEE_BPS: 500,
  MAX_PERFORMANCE_FEE_BPS: 5000,
} as const;

export const CREATOR_FEE_CAPS: Record<
  string,
  { maxMgmt: number; maxPerf: number }
> = {
  Unverified: { maxMgmt: 100, maxPerf: 2000 },
  Basic: { maxMgmt: 200, maxPerf: 3000 },
  Verified: { maxMgmt: 300, maxPerf: 4000 },
  Trusted: { maxMgmt: 500, maxPerf: 5000 },
  Sovereign: { maxMgmt: 500, maxPerf: 5000 },
};

export const SEEDED_VAULTS = [
  {
    name: "Alice's USDC Yield Vault",
    agentId: 1,
    baseAsset: "USDC",
    mgmtBps: 200,
    perfBps: 2000,
    risk: 3,
  },
  {
    name: "Bob's WETH Growth Vault",
    agentId: 2,
    baseAsset: "WETH",
    mgmtBps: 300,
    perfBps: 3000,
    risk: 6,
  },
  {
    name: "Carol's Multi-Asset Vault",
    agentId: 3,
    baseAsset: "USDC",
    mgmtBps: 100,
    perfBps: 1500,
    risk: 2,
  },
] as const;

/** Look up the risk rating for a seeded vault by agent ID. */
export function getSeededVaultRiskRating(agentId: number): number | null {
  const vault = SEEDED_VAULTS.find((v) => v.agentId === agentId);
  return vault ? vault.risk : null;
}

export const DEPOSITOR_DISCOUNTS: Record<
  string,
  { mgmt: number; perf: number }
> = {
  Unverified: { mgmt: 0, perf: 0 },
  Basic: { mgmt: 5, perf: 5 },
  Verified: { mgmt: 10, perf: 10 },
  Trusted: { mgmt: 20, perf: 15 },
  Sovereign: { mgmt: 30, perf: 25 },
};
